<template>
  <section class="search-section">
    <div class="form-box">
      <form @submit.prevent="handleSearch" autocomplete="off">
        <h2>User Details</h2>
        <div class="form-content">
          <!-- User ID Field -->
          <div class="inputbox">
            <input
              type="text"
              id="userId"
              placeholder="User ID"
              v-model="searchCriteria.userId"
            />
          </div>

          <!-- First Name Field -->
          <div class="inputbox">
            <input
              type="text"
              id="firstname"
              placeholder="Firstname"
              v-model="searchCriteria.firstName"
            />
          </div>

          <!-- Last Name Field -->
          <div class="inputbox">
            <input
              type="text"
              id="lastname"
              placeholder="Lastname"
              v-model="searchCriteria.lastName"
            />
          </div>

          <!-- Email Field -->
          <div class="inputbox">
            <input
              type="text"
              id="email"
              placeholder="Email Address"
              v-model="searchCriteria.emailAddress"
            />
          </div>

          <!-- Email Field -->
          <div class="inputbox">
            <input
              type="text"
              id="phoneNumber"
              placeholder="Tel No..."
              v-model="searchCriteria.phoneNumber"
            />
          </div>         

          <!-- Search Button -->
          <div class="button-container">
            <button :disabled="isSubmitting" type="submit">
              <span v-if="!isSubmitting">Search</span>
              <LoaderDots v-if="isSubmitting" />
            </button>
          </div>
        </div>
      </form>
    </div>

    <!-- Results Section -->
    <div class="results">
      <div class="result-title">Search Results</div>
      <div class="table-content">
        <table>
          <thead>
            <tr class="table-header">
              <th>UserID</th>
              <th>Email</th>
              <th>Name</th>
              <th>Date Registered</th>
              <th>Tel No...</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              class="table-row"
              v-for="user in userDetails"
              :key="user.userId"
            >
              <td>{{ user.userId }}</td>
              <td>{{ user.emailAddress }}</td>
              <td>{{ user.firstName }} {{ user.lastName }}</td>
              <td>{{ user.formattedCreateDate }}</td>
              <td>{{ user.phoneNumber }}</td>
              <td class="icon">
                <a :href="`/users/${user.userId}`">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                    <!--!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.-->
                    <path
                      d="M495.9 166.6c3.2 8.7 .5 18.4-6.4 24.6l-43.3 39.4c1.1 8.3 1.7 16.8 1.7 25.4s-.6 17.1-1.7 25.4l43.3 39.4c6.9 6.2 9.6 15.9 6.4 24.6c-4.4 11.9-9.7 23.3-15.8 34.3l-4.7 8.1c-6.6 11-14 21.4-22.1 31.2c-5.9 7.2-15.7 9.6-24.5 6.8l-55.7-17.7c-13.4 10.3-28.2 18.9-44 25.4l-12.5 57.1c-2 9.1-9 16.3-18.2 17.8c-13.8 2.3-28 3.5-42.5 3.5s-28.7-1.2-42.5-3.5c-9.2-1.5-16.2-8.7-18.2-17.8l-12.5-57.1c-15.8-6.5-30.6-15.1-44-25.4L83.1 425.9c-8.8 2.8-18.6 .3-24.5-6.8c-8.1-9.8-15.5-20.2-22.1-31.2l-4.7-8.1c-6.1-11-11.4-22.4-15.8-34.3c-3.2-8.7-.5-18.4 6.4-24.6l43.3-39.4C64.6 273.1 64 264.6 64 256s.6-17.1 1.7-25.4L22.4 191.2c-6.9-6.2-9.6-15.9-6.4-24.6c4.4-11.9 9.7-23.3 15.8-34.3l4.7-8.1c6.6-11 14-21.4 22.1-31.2c5.9-7.2 15.7-9.6 24.5-6.8l55.7 17.7c13.4-10.3 28.2-18.9 44-25.4l12.5-57.1c2-9.1 9-16.3 18.2-17.8C227.3 1.2 241.5 0 256 0s28.7 1.2 42.5 3.5c9.2 1.5 16.2 8.7 18.2 17.8l12.5 57.1c15.8 6.5 30.6 15.1 44 25.4l55.7-17.7c8.8-2.8 18.6-.3 24.5 6.8c8.1 9.8 15.5 20.2 22.1 31.2l4.7 8.1c6.1 11 11.4 22.4 15.8 34.3zM256 336a80 80 0 1 0 0-160 80 80 0 1 0 0 160z"
                    />
                  </svg>
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import api from "../configurations/axios"; // Import your Axios instance
import Alert from "sweetalert2"; // Import SweetAlert2 for notifications
import UserService from "../services/user-service";
import LoaderDots from "./LoadingButtonDots.vue";

export default defineComponent({
  components: {
    LoaderDots,
  },
  setup() {
    // Search criteria object bound to the form inputs
    const searchCriteria = ref({
      firstName: "",
      lastName: "",
      emailAddress: "",
      phoneNumber: "",
      userId: null,
    });

    const isSubmitting = ref(false); // New state for button disabling

    // Array to store the search results
    const userDetails = ref([]);

    // Method to handle the search form submission
    const handleSearch = async () => {
      const isAnyFieldFilled = Object.values(searchCriteria.value).some(
        (value) => value.trim() !== ""
      );

      if (!isAnyFieldFilled) {
        Alert.fire(
          "Validation Error",
          "At least one field must have a value to perform the search.",
          "error"
        );
        return; // Prevent form submission if no fields are filled
      }

      try {
        isSubmitting.value = true;
        console.log("Search", searchCriteria.value);
        const response = await UserService.getUserDetails(searchCriteria.value);

        if (response) {
          userDetails.value = response.data;
        }

        // Display a message if no users are found
        if (userDetails.value.length === 0) {
          Alert.fire(
            "No Results",
            "No users matched the search criteria.",
            "info"
          );
        }
        isSubmitting.value = false;
      } catch (error) {
        console.error("Error fetching user details:", error);
        Alert.fire(
          "Error",
          "Failed to fetch user details. Please try again.",
          "error"
        );
        isSubmitting.value = false;
      }
    };

    return {
      searchCriteria,
      userDetails,
      handleSearch,
      isSubmitting,
    };
  },
});
</script>

<style scoped>
.icon svg {
  display: block;
  position: relative;
  min-width: 40px;
  height: 100%;
  fill: var(--clr-black2);
}

.icon svg {
  height: 30px;
}

.search-section {
  display: grid;
  grid-template-columns: 1fr;
  padding: 20px;
  gap: 2rem;
}

.form-box {
  padding: 30px 20px 30px 20px;
  width: 100%;
}

.form-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

h2 {
  font-size: 1.8em;
  color: #726e6e;
  margin-bottom: 20px;
}

.inputbox {
  position: relative;
  border-bottom: 2px solid #9d9c9c;
}

.inputbox input {
  width: 100%;
  height: 65px;
  background: transparent;
  border: none;
  border-radius: 3px;
  outline: none;
  font-size: 1em;
  padding: 0 10px;
  color: #333;
  transition: border-color 0.3s ease;
}

.button-container {
  display: flex;
  justify-content: center;
  
}

.button-container button {
  min-width: 100%;
  height: 65px;
  border-radius: 3px;
  background-color: var(--clr-primary);
  color: #fff;
  border: none;
  cursor: pointer;
  font-size: 1em;
  font-weight: 600;
  transition: background-color 0.3s ease, transform 0.2s ease;
  box-shadow: 0 10px 30px -12px rgba(0, 0, 0, 0.42),
    0 4px 25px 0px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2);
}

button {
  border: none;
  cursor: pointer;
}

button:hover {
  filter: brightness(0.8);
  transform: translateY(-2px);
}

button:active {
  transform: translateY(0);
}

.result-title {
  position: relative;
  background-color: var(--clr-primary);
  padding: 1rem 1rem;
  margin-inline: 2rem;
  color: white;
  font-size: 24px;
  border-radius: 3px;
  box-shadow: 0 10px 30px -12px rgba(0, 0, 0, 0.42),
    0 4px 25px 0px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2);
  z-index: 1;
}

.table-content {
  overflow-x: auto;
  position: relative;
  background-color: #fff;
  padding: 60px 40px 20px 40px;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.14);
  border-radius: 3px;
  margin-top: -50px;
}

table {
  width: 100%;
  border-collapse: collapse;
  background-color: white;
}

td {
  padding: 0.75rem 1rem;
  text-align: left;
  border-top: 1px solid #ddd;
}

th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: bold;
}

.table-row:hover {
  background-color: #f1f1f1;
}

@media (max-width: 768px) {
  .form-box {
    padding: 10px;
  }

  th,
  td {
    padding: 0.5rem 0.75rem;
  }

  .results {
    overflow-x: scroll;
  }

  .result-title {
    padding: 0.5rem 1rem;
    text-align: center;
    font-size: 16px;
    margin-inline: 0.7rem;
  }

  .table-content {
    padding-inline: 10px;
    margin-top: -30px;
  }

  .search-section {
    padding: 10px;
  }
}
</style>
